import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Cell,
  ResponsiveContainer,
  Tooltip,
  CartesianGrid,
  Label,
} from 'recharts';
import { COLOR_GREY_200, COLOR_GREY_600 } from 'src/colors';
import { DataChartTooltip } from 'src/App/HOC/Tooltips';
import { abbreviateNumber, formatCurrency } from 'src/utils/functions';

const BiaxialHorizontalBarChart = ({
  data,
  units,
  label,
  height,
  barColors,
  barAccentColors,
  useShortHand,
  isCurrency,
  isUserAllowed,
  multipleMarkersCallLoading
}) => {
  const [focusBar, setFocusBar] = useState(null);

  const dummyData = [
    {
      'Occupancy Rate': 21,
      Revenue: 25000,
      keyLong: 'Studio',
      keyShort: 'Studio',
    },
    {
      'Occupancy Rate': 51,
      Revenue: 35000,
      keyLong: '1 bedroom',
      keyShort: '1',
    },
    {
      'Occupancy Rate': 75,
      Revenue: 45000,
      keyLong: '2 bedrooms',
      keyShort: '2',
    },
    {
      'Occupancy Rate': 31,
      Revenue: 12000,
      keyLong: '3 bedrooms',
      keyShort: '3',
    },
    {
      'Occupancy Rate': 59,
      Revenue: 65000,
      keyLong: '4 bedrooms',
      keyShort: '4',
    },
    {
      'Occupancy Rate': 11,
      Revenue: 6000,
      keyLong: '6+ bedrooms',
      keyShort: '6+',
    },
    {
      'Occupancy Rate': 82,
      Revenue: 73000,
      keyLong: 'private/hotel/shared rooms',
      keyShort: 'rooms',
    },
  ];

  const dataKeys = Object.keys(data[0]).filter(key => key != 'keyShort' && key != 'keyLong');

  const CustomTooltip = ({ active, payload, label, classes }) => {
    if (active && payload) {
      const tooltipData = dataKeys.map((dataKey, index) => ({
        dataKey: dataKey,
        val: payload[0].payload[dataKey]
          ? isCurrency[index]
            ? `${units[index]} ${formatCurrency(payload[0].payload[dataKey].toString())}`
            : `${formatCurrency(payload[0].payload[dataKey].toString())} ${units[index]}`
          : '-',
        color: barAccentColors[index],
      }));
      return <DataChartTooltip data={tooltipData} />;
    }
    return null;
  };

  return (
    <ResponsiveContainer height={height} width="100%">
      <BarChart
        layout="horizontal"
        width="100%"
        height="100%"
        data={isUserAllowed ? data : dummyData}
        margin={{ top: 5, right: 0, bottom: 5, left: -30 }}
        onMouseMove={state => {
          if (state.isTooltipActive) {
            setFocusBar(state.activeTooltipIndex);
          } else {
            setFocusBar(null);
          }
        }}
      >
        <XAxis
          dataKey={useShortHand ? 'keyShort' : 'keyLong'}
          style={{
            fontSize: '0.75rem',
            fontFamily: 'Roboto',
          }}
          axisLine={false}
          tickLine={false}
          type="category"
        >
          {label && (
            <Label
              value={label}
              offset={0}
              position="insideBottom"
              style={{
                fontSize: '0.75rem',
                fontFamily: 'Roboto',
              }}
              fill={COLOR_GREY_600}
            />
          )}
        </XAxis>
        <YAxis
          yAxisId="left"
          orientation="left"
          style={{
            fontSize: '0.75rem',
            fontFamily: 'Roboto',
          }}
          axisLine={false}
          tickLine={false}
          type="number"
          tickSize={isCurrency[0] ? 2 : 4}
          tickFormatter={value => (isCurrency[0] ? abbreviateNumber(value, false) : value)}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          style={{
            fontSize: '0.75rem',
            fontFamily: 'Roboto',
          }}
          axisLine={false}
          tickLine={false}
          type="number"
          tickSize={isCurrency[1] ? 2 : 4}
          tickFormatter={value => (isCurrency[1] ? abbreviateNumber(value, false) : value)}
        />
        <CartesianGrid vertical={false} horizontal={true} stroke={COLOR_GREY_200} />
        <Tooltip content={<CustomTooltip classes active payload label />} cursor={false} />
        <Bar
          yAxisId="left"
          dataKey={dataKeys[0]}
          isAnimationActive={true}
          animationDuration={150}
          radius={[13, 13, 13, 13]}
          barSize={13}
          style={{
            cursor: 'pointer',
          }}
        >
          {data.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={focusBar === index ? barAccentColors[0] : barColors[0]}
            />
          ))}
        </Bar>
        <Bar
          yAxisId="right"
          dataKey={dataKeys[1]}
          isAnimationActive={true}
          animationDuration={150}
          radius={[13, 13, 13, 13]}
          barSize={13}
          style={{
            cursor: 'pointer',
          }}
        >
          {data.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={focusBar === index ? barAccentColors[1] : barColors[1]}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export default BiaxialHorizontalBarChart;
